import { useState, useEffect } from 'react';
import type { GanttTask, CreateGanttTaskInput, UpdateGanttTaskInput } from '../../types';

interface GanttTaskFormProps {
  task?: GanttTask | null;
  projectId: string;
  tasks: GanttTask[]; // For dependency selection
  onSubmit: (data: CreateGanttTaskInput | UpdateGanttTaskInput) => Promise<void>;
  onDelete?: () => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const PRESET_COLORS = [
  { value: '#3B82F6', label: 'Blu' },
  { value: '#10B981', label: 'Verde' },
  { value: '#F59E0B', label: 'Ambra' },
  { value: '#EF4444', label: 'Rosso' },
  { value: '#8B5CF6', label: 'Viola' },
  { value: '#EC4899', label: 'Rosa' },
  { value: '#06B6D4', label: 'Ciano' },
  { value: '#F97316', label: 'Arancione' },
];

function formatDateForInput(dateStr: string | undefined): string {
  if (!dateStr) {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }
  return dateStr.split('T')[0];
}

export function GanttTaskForm({
  task,
  projectId,
  tasks,
  onSubmit,
  onDelete,
  onCancel,
  isLoading
}: GanttTaskFormProps) {
  const isEditing = !!task;

  const [nome, setNome] = useState(task?.nome || '');
  const [dataInizio, setDataInizio] = useState(formatDateForInput(task?.dataInizio));
  const [dataFine, setDataFine] = useState(formatDateForInput(task?.dataFine));
  const [percentuale, setPercentuale] = useState(task?.percentuale || 0);
  const [oreStimate, setOreStimate] = useState(task?.oreStimate ?? 0);
  const [durataFissa, setDurataFissa] = useState(task?.durataFissaGiorni != null);
  const [durataFissaGiorni, setDurataFissaGiorni] = useState(task?.durataFissaGiorni ?? 1);
  const [dipendeDa, setDipendeDa] = useState(task?.dipendeDa || '');
  const [colore, setColore] = useState(task?.colore || '#3B82F6');
  const [note, setNote] = useState(task?.note || '');

  // Reset form when task changes
  useEffect(() => {
    if (task) {
      setNome(task.nome);
      setDataInizio(formatDateForInput(task.dataInizio));
      setDataFine(formatDateForInput(task.dataFine));
      setPercentuale(task.percentuale);
      setOreStimate(task.oreStimate ?? 0);
      setDurataFissa(task.durataFissaGiorni != null);
      setDurataFissaGiorni(task.durataFissaGiorni ?? 1);
      setDipendeDa(task.dipendeDa || '');
      setColore(task.colore || '#3B82F6');
      setNote(task.note || '');
    }
  }, [task]);

  // Available tasks for dependencies (exclude self)
  const availableDependencies = tasks.filter(t => t.id !== task?.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      nome,
      dataInizio,
      dataFine,
      percentuale,
      oreStimate,
      durataFissaGiorni: durataFissa ? Math.max(1, durataFissaGiorni) : null,
      dipendeDa: dipendeDa || undefined,
      colore: colore || undefined,
      note: note || undefined,
      ...(isEditing ? {} : { projectId })
    };

    await onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Nome */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Nome attività
        </label>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Es: Rogito notarile"
          required
          maxLength={100}
        />
      </div>

      {/* Date */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data inizio
          </label>
          <input
            type="date"
            value={dataInizio}
            onChange={(e) => setDataInizio(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Data fine
          </label>
          <input
            type="date"
            value={dataFine}
            onChange={(e) => setDataFine(e.target.value)}
            min={dataInizio}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>
      </div>

      {/* Percentuale */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Progresso: {percentuale}%
        </label>
        <input
          type="range"
          value={percentuale}
          onChange={(e) => setPercentuale(Number(e.target.value))}
          min="0"
          max="100"
          step="5"
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
        />
        <div className="flex justify-between text-xs text-gray-500 mt-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Carico di lavoro (effort-driven) */}
      <div className="rounded-lg border border-gray-200 p-3 space-y-3 bg-gray-50">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Carico di lavoro (ore stimate)
          </label>
          <input
            type="number"
            value={oreStimate}
            onChange={(e) => setOreStimate(Math.max(0, Number(e.target.value)))}
            min="0"
            step="1"
            disabled={durataFissa}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-400"
            placeholder="Es: 40"
          />
          <p className="text-xs text-gray-500 mt-1">
            La durata viene calcolata da ore ÷ capacità settimanale del cantiere.
          </p>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={durataFissa}
            onChange={(e) => setDurataFissa(e.target.checked)}
            className="rounded border-gray-300"
          />
          Durata fissa in giorni (lavoro di terzi o a maturazione)
        </label>

        {durataFissa && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Durata fissa (giorni)
            </label>
            <input
              type="number"
              value={durataFissaGiorni}
              onChange={(e) => setDurataFissaGiorni(Math.max(1, Number(e.target.value)))}
              min="1"
              step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
        )}
      </div>

      {/* Dipende da */}
      {availableDependencies.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Dipende da (opzionale)
          </label>
          <select
            value={dipendeDa}
            onChange={(e) => setDipendeDa(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Nessuna dipendenza</option>
            {availableDependencies.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nome}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Colore */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Colore
        </label>
        <div className="flex gap-2 flex-wrap">
          {PRESET_COLORS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setColore(value)}
              className={`w-8 h-8 rounded-full border-2 transition-transform ${
                colore === value ? 'border-gray-800 scale-110' : 'border-transparent'
              }`}
              style={{ backgroundColor: value }}
              title={label}
            />
          ))}
        </div>
      </div>

      {/* Note */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Note (opzionale)
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Annotazioni aggiuntive..."
          maxLength={500}
        />
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        {isEditing && onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            className="px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            disabled={isLoading}
          >
            Elimina
          </button>
        ) : (
          <div />
        )}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={isLoading}
          >
            Annulla
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={isLoading || !nome.trim()}
          >
            {isLoading ? 'Salvataggio...' : isEditing ? 'Salva' : 'Aggiungi'}
          </button>
        </div>
      </div>
    </form>
  );
}
